import logger from '../utils/logger';

/**
 * Simple IndexedDB service for local caching of large datasets.
 */
const DB_NAME = 'YeloUnifiedDB';
const DB_VERSION = 2;
const STORES = {
    CONTRACTS: 'contracts',
    FLEET: 'fleet',
    MAINTENANCE: 'maintenance',
    METADATA: 'metadata'
};

const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            // Create object stores if they don't exist
            Object.values(STORES).forEach(storeName => {
                if (!db.objectStoreNames.contains(storeName)) {
                    db.createObjectStore(storeName);
                }
            });
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

export const dbService = {
    /**
     * Saves a collection of items to a specific store.
     * @param {string} storeName 
     * @param {any} data 
     */
    async save(storeName, data) {
        try {
            const db = await openDB();
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);

            // Clear existing data first
            store.clear();

            // Put new data (we use a single key 'all' for the whole array for simplicity given our usage)
            store.put(data, 'all');

            // Update last_sync metadata
            const metaTx = db.transaction(STORES.METADATA, 'readwrite');
            metaTx.objectStore(STORES.METADATA).put(new Date().toISOString(), `last_sync_${storeName}`);

            return new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            });
        } catch (error) {
            logger.error(`IndexedDB Save Error (${storeName}):`, error);
        }
    },

    /**
     * Loads data from a specific store.
     * @param {string} storeName 
     * @returns {Promise<any|null>}
     */
    async load(storeName) {
        try {
            const db = await openDB();
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const request = store.get('all');

            return new Promise((resolve, reject) => {
                request.onsuccess = () => resolve(request.result || null);
                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            logger.error(`IndexedDB Load Error (${storeName}):`, error);
            return null;
        }
    },

    /**
     * Gets sync metadata
     * @param {string} key 
     */
    async getMeta(key) {
        try {
            const db = await openDB();
            const tx = db.transaction(STORES.METADATA, 'readonly');
            const store = tx.objectStore(STORES.METADATA);
            const request = store.get(key);

            return new Promise((resolve) => {
                request.onsuccess = () => resolve(request.result);
                request.onerror = () => resolve(null);
            });
        } catch { return null; }
    },

    /**
     * Sets sync metadata
     * @param {string} key 
     * @param {any} value 
     */
    async setMeta(key, value) {
        try {
            const db = await openDB();
            const tx = db.transaction(STORES.METADATA, 'readwrite');
            const store = tx.objectStore(STORES.METADATA);
            store.put(value, key);

            return new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            });
        } catch (error) {
            logger.error(`IndexedDB SetMeta Error (${key}):`, error);
        }
    },

    /**
     * Clears all stores
     */
    async clear() {
        const db = await openDB();
        Object.values(STORES).forEach(storeName => {
            const tx = db.transaction(storeName, 'readwrite');
            tx.objectStore(storeName).clear();
        });
    }
};
